import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Button from 'flarum/common/components/Button';
import ItemList from 'flarum/common/utils/ItemList';
import AddTranslationModal from './components/AddTranslationModal';
import LanguageSwitcher from './components/LanguageSwitcher';

app.initializers.add('predictiontalk-post-translations', () => {
    // Add URL-based language switcher to header
    extend(HeaderSecondary.prototype, 'items', function (items: ItemList<any>) {
        items.add('languageSwitcher', <LanguageSwitcher />, 20);

        // Hide Flarum's built-in locale selector (it only sets cookie, doesn't change URL)
        if (items.has('locale')) {
            items.remove('locale');
        }
    });

    // Add translate button to post actions (admin or post author only)
    extend(CommentPost.prototype, 'actionItems', function (items: ItemList<any>) {
        const post = this.attrs.post;
        if (!post || !app.session.user) return;

        // Only show to admin (group 1) or post author
        const isAdmin = app.session.user.groups()?.some((g: any) => g.id() === '1');
        const isAuthor = post.user() && post.user().id() === app.session.user.id();
        if (!isAdmin && !isAuthor) return;

        items.add('translate', (
            <Button
                className="Button Button--link Button--translate"
                icon="fas fa-language"
                onclick={() => app.modal.show(AddTranslationModal, { post })}
            >
                {app.translator.trans('predictiontalk-post-translations.forum.post.translate_button')}
            </Button>
        ), -10);
    });

    // Add clickable translation badges above post content
    extend(CommentPost.prototype, 'headerItems', function (items: ItemList<any>) {
        const post = this.attrs.post;
        if (!post) return;

        const availableTranslations: string[] = post.attribute('availableTranslations') || [];
        const translationLocale: string | null = post.attribute('translationLocale') || null;

        // Map DB locale codes to URL prefixes
        const localeToPrefix: Record<string, string> = { uk: 'ua', 'pt-BR': 'pt' };

        if (availableTranslations.length > 0) {
            const basePath = window.location.pathname.replace(/^\/(ua|hi|es|pt|tr|ko|vi)(\/|$)/, '/');
            const isEnglish = !translationLocale;

            // EN badge first — links to original (no prefix)
            const allBadges = [
                <a
                    href={basePath}
                    className={'PostTranslation-langBadge' + (isEnglish ? ' active' : '')}
                    title="EN"
                >
                    EN
                </a>
            ];

            // Translation badges
            availableTranslations.forEach((locale: string) => {
                const prefix = localeToPrefix[locale] || locale;
                const href = '/' + prefix + (basePath.startsWith('/') ? basePath : '/' + basePath);
                const isActive = translationLocale === locale;

                allBadges.push(
                    <a
                        href={href}
                        className={'PostTranslation-langBadge' + (isActive ? ' active' : '')}
                        title={prefix.toUpperCase()}
                    >
                        {prefix.toUpperCase()}
                    </a>
                );
            });

            items.add('translationBadges', (
                <span className="PostTranslation-langBadges">{allBadges}</span>
            ), -100);
        }
    });

    // Show "viewing translation" indicator
    extend(CommentPost.prototype, 'content', function (vdom: any[]) {
        const post = this.attrs.post;
        if (!post) return;

        const translationLocale: string | null = post.attribute('translationLocale') || null;

        if (translationLocale) {
            const localeNames: Record<string, string> = {
                uk: '🇺🇦 Ukrainian', ru: '🇷🇺 Russian', es: '🇪🇸 Spanish',
                de: '🇩🇪 German', fr: '🇫🇷 French', pt: '🇧🇷 Portuguese',
                zh: '🇨🇳 Chinese', ja: '🇯🇵 Japanese', ko: '🇰🇷 Korean', ar: '🇸🇦 Arabic',
                hi: '🇮🇳 Hindi', tr: '🇹🇷 Turkish', vi: '🇻🇳 Vietnamese',
            };
            const localeName = localeNames[translationLocale] || translationLocale.toUpperCase();

            vdom.splice(1, 0, (
                <div className="PostTranslation-indicator">
                    <i className="fas fa-language icon" />
                    {localeName}
                </div>
            ));
        }
    });
});
